<template>
  <div class="recipe-detail-view">
    <!-- Tillbaka-knapp -->
    <button class="back-button" @click="$emit('back')" type="button">
      <span class="back-arrow">←</span> Tillbaka till recepten
    </button>

    <article class="recipe-detail-card">
      <!-- Topp / Hero -->
      <header class="detail-header">
        <div class="header-tags">
          <span class="category-badge">
            {{ recipe.categoryIcon }} {{ recipe.categoryName }}
          </span>
          <span class="difficulty-badge">📊 {{ recipe.difficulty }}</span>
        </div>

        <h1 class="recipe-title">{{ recipe.title }}</h1>
        <p class="recipe-lead">{{ recipe.description }}</p>

        <div class="recipe-quick-meta">
          <div class="meta-box" v-if="recipe.prepTime">
            <span class="meta-label">Förberedelse</span>
            <span class="meta-value">⏱️ {{ recipe.prepTime }}</span>
          </div>
          <div class="meta-box" v-if="recipe.cookTime">
            <span class="meta-label">Tillagning</span>
            <span class="meta-value">🍳 {{ recipe.cookTime }}</span>
          </div>
          <div class="meta-box" v-if="recipe.totalTime">
            <span class="meta-label">Total tid</span>
            <span class="meta-value">⌛ {{ recipe.totalTime }}</span>
          </div>
        </div>
      </header>

      <!-- Stor bild -->
      <div class="detail-image-container">
        <img
          :src="recipe.imageUrl"
          :alt="recipe.title"
          class="detail-image"
          @error="handleImageError"
        />
      </div>

      <!-- Innehåll: Ingredienser & instruktioner -->
      <div class="recipe-content-grid">
        <!-- Vänster kolumn: Ingredienser & portionsberäknare -->
        <aside class="ingredients-panel">
          <div class="servings-selector">
            <div class="servings-info">
              <span class="servings-label">Portioner:</span>
              <span class="servings-current">{{ currentServings }} st</span>
            </div>
            <div class="servings-controls">
              <button
                class="serving-btn"
                :disabled="currentServings <= 1"
                @click="changeServings(-1)"
                title="Minska portioner"
                type="button"
                aria-label="Minska portioner"
              >
                −
              </button>
              <button
                class="serving-btn"
                :disabled="currentServings >= 24"
                @click="changeServings(1)"
                title="Öka portioner"
                type="button"
                aria-label="Öka portioner"
              >
                +
              </button>
            </div>
          </div>

          <h2 class="section-heading">Ingredienser</h2>

          <ul class="ingredients-list">
            <li
              v-for="(item, index) in scaledIngredients"
              :key="index"
              :class="['ingredient-item', { checked: checkedIngredients[index] }]"
              @click="toggleIngredient(index)"
            >
              <input
                type="checkbox"
                :checked="checkedIngredients[index]"
                @change="toggleIngredient(index)"
                @click.stop
                class="ingredient-checkbox"
                :id="'ing-' + index"
              />
              <label :for="'ing-' + index" class="ingredient-text" @click.stop="toggleIngredient(index)">
                <strong v-if="item.amount" class="ingredient-amount">
                  {{ formatAmount(item.amount) }} {{ item.unit }}
                </strong>
                <span class="ingredient-name">{{ item.name }}</span>
              </label>
            </li>
          </ul>
        </aside>

        <!-- Höger kolumn: Instruktioner -->
        <section class="instructions-panel">
          <h2 class="section-heading">Gör så här</h2>
          <p class="section-sub">Följ stegen i tur och ordning:</p>

          <ol class="steps-list">
            <li
              v-for="(step, index) in recipe.instructions"
              :key="index"
              :class="['step-item', { completed: completedSteps[index] }]"
              @click="toggleStep(index)"
            >
              <div class="step-number">{{ index + 1 }}</div>
              <div class="step-body">
                <p class="step-text">{{ step }}</p>
              </div>
            </li>
          </ol>

          <!-- Tipsruta om tips finns -->
          <div v-if="recipe.tips" class="tips-box">
            <div class="tips-icon">💡</div>
            <div class="tips-content">
              <h4>Kockens tips:</h4>
              <p>{{ recipe.tips }}</p>
            </div>
          </div>
        </section>
      </div>
    </article>
  </div>
</template>

<script>
export default {
  name: 'RecipeDetail',
  props: {
    recipe: {
      type: Object,
      required: true
    }
  },
  emits: ['back'],
  data() {
    return {
      currentServings: this.recipe.servings || 4,
      checkedIngredients: {},
      completedSteps: {}
    }
  },
  computed: {
    scaledIngredients() {
      const baseServings = this.recipe.servings || 4;
      const ratio = this.currentServings / baseServings;

      return this.recipe.ingredients.map(item => {
        if (typeof item.amount === 'number') {
          return {
            ...item,
            amount: item.amount * ratio
          };
        }
        return item;
      });
    }
  },
  watch: {
    recipe: {
      immediate: true,
      handler(newRecipe) {
        this.currentServings = newRecipe.servings || 4;
        this.checkedIngredients = {};
        this.completedSteps = {};
      }
    }
  },
  methods: {
    changeServings(delta) {
      const updated = this.currentServings + delta;
      if (updated >= 1 && updated <= 24) {
        this.currentServings = updated;
      }
    },
    formatAmount(amount) {
      if (Number.isInteger(amount)) {
        return amount.toString();
      }
      const rounded = Math.round(amount * 10) / 10;
      return rounded.toString().replace('.', ',');
    },
    toggleIngredient(index) {
      this.checkedIngredients[index] = !this.checkedIngredients[index];
    },
    toggleStep(index) {
      this.completedSteps[index] = !this.completedSteps[index];
    },
    handleImageError(event) {
      event.target.style.display = 'none';
    }
  }
}
</script>

<style scoped>
.recipe-detail-view {
  padding: 16px 0 60px 0;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 10px 18px;
  background-color: var(--color-card-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--color-text-main);
  margin-bottom: 20px;
  box-shadow: var(--shadow-sm);
  touch-action: manipulation;
}

.back-button:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
  transform: translateX(-3px);
}

.back-button:active {
  transform: scale(0.98);
}

.recipe-detail-card {
  background-color: var(--color-card-bg);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow-md);
  overflow: hidden;
}

.detail-header {
  padding: 32px 32px 20px 32px;
}

.header-tags {
  display: flex;
  gap: 10px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.category-badge, .difficulty-badge {
  background-color: var(--color-bg-body);
  border: 1px solid var(--color-border);
  padding: 4px 12px;
  border-radius: var(--radius-lg);
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--color-text-main);
}

.recipe-title {
  font-size: clamp(1.5rem, 5vw, 2.2rem);
  font-weight: 800;
  color: var(--color-text-main);
  line-height: 1.25;
  margin-bottom: 10px;
}

.recipe-lead {
  font-size: 1.05rem;
  color: var(--color-text-muted);
  line-height: 1.55;
  max-width: 800px;
  margin-bottom: 20px;
}

.recipe-quick-meta {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.meta-box {
  background-color: var(--color-bg-body);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  padding: 8px 14px;
  display: flex;
  flex-direction: column;
}

.meta-label {
  font-size: 0.72rem;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}

.meta-value {
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--color-text-main);
}

.detail-image-container {
  width: 100%;
  max-height: 400px;
  background-color: #f5efe6;
  overflow: hidden;
}

.detail-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  max-height: 400px;
}

.recipe-content-grid {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 36px;
  padding: 32px;
}

.section-heading {
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--color-text-main);
  margin-bottom: 4px;
}

.section-sub {
  font-size: 0.88rem;
  color: var(--color-text-muted);
  margin-bottom: 18px;
}

/* Servings Selector */
.servings-selector {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--color-primary-light);
  border: 1px solid rgba(230, 81, 0, 0.2);
  border-radius: var(--radius-sm);
  padding: 12px 16px;
  margin-bottom: 22px;
}

.servings-label {
  font-size: 0.9rem;
  color: var(--color-text-muted);
  margin-right: 6px;
}

.servings-current {
  font-weight: 700;
  color: var(--color-primary);
  font-size: 1.05rem;
}

.servings-controls {
  display: flex;
  gap: 8px;
}

.serving-btn {
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  border: 1px solid rgba(230, 81, 0, 0.3);
  border-radius: 8px;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-primary);
  touch-action: manipulation;
}

.serving-btn:hover:not(:disabled) {
  background-color: var(--color-primary);
  color: #ffffff;
}

.serving-btn:active:not(:disabled) {
  transform: scale(0.95);
}

.serving-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Ingredients */
.ingredients-list {
  list-style: none;
}

.ingredient-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 10px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  border-bottom: 1px solid var(--color-border);
  transition: background-color 0.15s ease;
  touch-action: manipulation;
}

.ingredient-item:hover {
  background-color: var(--color-bg-body);
}

.ingredient-checkbox {
  accent-color: var(--color-primary);
  cursor: pointer;
  width: 19px;
  height: 19px;
  flex-shrink: 0;
}

.ingredient-text {
  font-size: 0.95rem;
  line-height: 1.45;
  user-select: none;
  cursor: pointer;
  flex-grow: 1;
}

.ingredient-amount {
  color: var(--color-text-main);
  margin-right: 6px;
}

.ingredient-name {
  color: var(--color-text-muted);
}

.ingredient-item.checked .ingredient-text {
  text-decoration: line-through;
  opacity: 0.55;
}

/* Instructions */
.steps-list {
  list-style: none;
}

.step-item {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 16px;
  margin-bottom: 12px;
  background-color: var(--color-bg-body);
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border);
  cursor: pointer;
  transition: all 0.2s ease;
  touch-action: manipulation;
}

.step-item:hover {
  border-color: rgba(230, 81, 0, 0.4);
}

.step-item:active {
  transform: scale(0.99);
}

.step-number {
  min-width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: var(--color-primary);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
  flex-shrink: 0;
  margin-top: 2px;
}

.step-body {
  flex-grow: 1;
}

.step-text {
  font-size: 0.98rem;
  line-height: 1.6;
  color: var(--color-text-main);
}

.step-item.completed {
  opacity: 0.6;
  background-color: #f0f0f0;
}

.step-item.completed .step-number {
  background-color: #888;
}

.step-item.completed .step-text {
  text-decoration: line-through;
}

/* Tips */
.tips-box {
  margin-top: 24px;
  background-color: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: var(--radius-sm);
  padding: 16px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.tips-icon {
  font-size: 1.4rem;
  line-height: 1;
  flex-shrink: 0;
}

.tips-content h4 {
  font-size: 0.95rem;
  color: #f57f17;
  margin-bottom: 4px;
}

.tips-content p {
  font-size: 0.9rem;
  color: #5d4037;
  line-height: 1.5;
}

@media (max-width: 860px) {
  .recipe-content-grid {
    grid-template-columns: 1fr;
    gap: 28px;
    padding: 24px;
  }
}

@media (max-width: 640px) {
  .recipe-detail-view {
    padding: 12px 0 40px 0;
  }
  .back-button {
    margin-bottom: 14px;
    font-size: 0.9rem;
    padding: 8px 14px;
  }
  .detail-header {
    padding: 18px 16px 14px 16px;
  }
  .recipe-lead {
    font-size: 0.95rem;
    margin-bottom: 16px;
  }
  .detail-image-container {
    max-height: 240px;
  }
  .detail-image {
    max-height: 240px;
  }
  .recipe-quick-meta {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(90px, 1fr));
    gap: 8px;
  }
  .meta-box {
    padding: 8px 10px;
    align-items: center;
    text-align: center;
  }
  .recipe-content-grid {
    padding: 18px 14px;
    gap: 24px;
  }
  .serving-btn {
    width: 42px;
    height: 42px;
  }
  .step-item {
    padding: 12px;
    gap: 12px;
  }
  .step-text {
    font-size: 0.93rem;
  }
}
</style>
